/**
 * One row in the run browser (backend issue #93). An eval run pins a set of
 * case/scenario versions and executes trials; the list reports progress and a
 * "contains failed cases" flag computed server-side as passed < completed.
 */
export type EvalRunSummary = {
  run_id: string
  created_at: string
  status: string
  model: string
  planned_trials: number
  completed_trials: number
  passed_trials: number
  /** Any recorded trial that did not pass (Failed or Flaky). */
  has_failures: boolean
}

export type EvalRunListResponse = {
  runs: EvalRunSummary[]
  total: number
  limit: number
  offset: number
}

/** Default page size for the run list (backend caps limit at 200). */
export const RUNS_PAGE_SIZE = 25

export type RunFilters = {
  /** "Contains failed cases" toggle; sent as has_failures=true when on. */
  hasFailures?: boolean
  page: number
  pageSize: number
}

export const getDefaultRunFilters = (): RunFilters => ({
  page: 1,
  pageSize: RUNS_PAGE_SIZE,
})

/**
 * Build the list query string. The backend returns runs newest-first; we only
 * send has_failures when the toggle is on (off means "show all runs").
 */
export const buildRunsListQuery = (filters: RunFilters): URLSearchParams => {
  const query = new URLSearchParams()
  if (filters.hasFailures) {
    query.set('has_failures', 'true')
  }
  query.set('limit', String(filters.pageSize))
  query.set('offset', String((filters.page - 1) * filters.pageSize))
  return query
}

export type EvalRunAccepted = {
  run_id: string
  status: string
  planned_trials: number
}

/**
 * Selection-based create payload (#94): a run is defined by selecting cases —
 * case_ids and/or tag_ids, combined as a union. Inference dimensions
 * (model / prompt_profile / inference_params) are server-defaulted, so the UI
 * doesn't send them.
 */
export type EvalRunCreateInput = {
  case_ids: string[]
  tag_ids: string[]
  include_mutants: boolean
  trials: number
}

/** Editor state for the define-a-run modal. */
export type RunSelectionForm = {
  caseIds: string[]
  tagIds: string[]
  includeMutants: boolean
  trials: number
}

export const emptyRunForm = (): RunSelectionForm => ({
  caseIds: [],
  tagIds: [],
  includeMutants: false,
  trials: 1,
})

/**
 * Whether the selection is empty (no cases and no tags). The backend rejects an
 * empty selection with 422; we mirror that to keep the submit button inert.
 */
export const isSelectionEmpty = (form: RunSelectionForm): boolean =>
  form.caseIds.length === 0 && form.tagIds.length === 0

export const buildRunCreateInput = (
  form: RunSelectionForm,
): EvalRunCreateInput => ({
  case_ids: form.caseIds,
  tag_ids: form.tagIds,
  include_mutants: form.includeMutants,
  trials: form.trials,
})

/** Passed-over-completed summary for a run row, e.g. "6/8". */
export const formatPassCount = (run: EvalRunSummary): string =>
  `${run.passed_trials}/${run.completed_trials}`

// --- run detail (#96) -------------------------------------------------------

export type EvalTrialSummary = {
  trial_id: string
  passed: boolean | null
  /** NULL == base-case trial; set == that mutant's golden_prompt_id (#89). */
  source_prompt_id: string | null
  outcome_verdicts: Record<string, unknown> | unknown[] | null
  judge_result: Record<string, unknown> | null
  token_usage: Record<string, unknown> | null
  transcript_uri: string | null
  audit_uri: string | null
}

/** One evaluation unit = (case, source_prompt_id) over its recorded trials. */
export type EvalUnitStatus = {
  source_prompt_id: string | null
  status: 'passed' | 'failed' | 'flaky'
  completed_trials: number
  passed_trials: number
  pass_rate: number
}

/** A parent case with its base + fanned-out mutant units grouped. */
export type EvalCaseUnits = {
  case_id: string | null
  case_name: string | null
  case_version_id: string
  units: EvalUnitStatus[]
}

/** Full run-detail payload from GET /v1/eval-runs/{id}. */
export type EvalRunDetail = {
  run_id: string
  suite: string
  model: string
  prompt_profile: string | null
  inference_params: Record<string, unknown>
  mock_server_version: string | null
  status: string
  tag: string | null
  planned_trials: number
  completed_trials: number
  passed_trials: number
  pass_rate: number
  total_input_tokens: number
  total_output_tokens: number
  pinned_cases: unknown[]
  created_at: string
  trials: EvalTrialSummary[]
  cases: EvalCaseUnits[]
}

/** Statuses a run will not move past, so the detail view can stop polling. */
const TERMINAL_STATUSES = ['complete', 'failed']

/** How often to poll a run's detail while it is still active. */
export const RUN_POLL_INTERVAL_MS = 3000

/** A run is active (worth polling) until it reaches a terminal status. */
export const isRunActive = (status: string): boolean =>
  !TERMINAL_STATUSES.includes(status)

/**
 * React Query refetch interval for a run's detail: poll while active, stop once
 * terminal (or while there is no data yet).
 */
export const runRefetchInterval = (
  status: string | undefined,
): number | false =>
  status !== undefined && isRunActive(status) ? RUN_POLL_INTERVAL_MS : false

/** Render a 0..1 pass rate as a rounded percentage, e.g. 0.75 -> "75%". */
export const formatPassRate = (rate: number): string =>
  `${Math.round(rate * 100)}%`

/** Ant Design Tag color for a per-unit Passed/Failed/Flaky status. */
export const unitStatusColor = (status: EvalUnitStatus['status']): string => {
  switch (status) {
    case 'passed':
      return 'green'
    case 'failed':
      return 'red'
    case 'flaky':
      return 'orange'
  }
}

/** A unit is a fanned-out mutant when it carries a source prompt id (#95). */
export const isMutantUnit = (unit: EvalUnitStatus): boolean =>
  unit.source_prompt_id != null

/** Short, readable handle for a mutant's source prompt id. */
export const mutantShortId = (sourcePromptId: string): string =>
  sourcePromptId.slice(0, 8)

/** Row label for a unit: the base case, or a mutant by its short id. */
export const unitLabel = (unit: EvalUnitStatus): string =>
  unit.source_prompt_id == null
    ? 'Base case'
    : `Mutant ${mutantShortId(unit.source_prompt_id)}`

/** Passed-over-completed summary for a unit, e.g. "3/5". */
export const formatUnitPassCount = (unit: EvalUnitStatus): string =>
  `${unit.passed_trials}/${unit.completed_trials}`

/** Ant Design Tag color for a run status. */
export const runStatusColor = (status: string): string => {
  switch (status) {
    case 'complete':
      return 'green'
    case 'running':
      return 'blue'
    case 'failed':
      return 'red'
    case 'queued':
      return 'default'
    default:
      return 'default'
  }
}
